import lod_ from "lodash";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import MDInput from "components/Basics/MDInput";
import { t } from "i18next";
import DragAndDrop from "pages/sources/components/DragAndDrop";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	CircularProgress,
	Tab,
	Tabs,
	Box,
	Fade,
	Alert,
	InputAdornment,
	IconButton,
	Icon
} from "@mui/material";
import MDAlert from "components/Basics/MDAlert";
import { selectMaxFileSizeByType } from "redux-react/reducers/applicationReducer";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import { checkIfSourcesExist } from "hooks/sources";
import SourceActions from "redux-react/actions/sourceActions";
/**
 * Import video/audio files - either by upload or by link
 * @param {object} props - Component props
 * @param {object} props.skeleton - Current skeleton state
 * @param {function} props.setSkeleton - Update skeleton
 * @param {function} props.validStep - Mark step as valid/invalid
 * @returns {JSX.Element} StepImport
 */
export default function StepImport({ skeleton, handleSave, validStep }) {
	const dispatch = useDispatch();

	const { mo: MAX_FILE_SIZE_MO, bytes: MAX_FILE_SIZE_BYTES } = useSelector(state =>
		selectMaxFileSizeByType(state, "videos")
	);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [tabValue, setTabValue] = useState(0);

	const [loadingProgress, setLoadingProgress] = useState(false);

	const [url, setUrl] = useState("");
	const [urlError, setUrlError] = useState(null);

	/**
	 * Download file from URL
	 */
	const downloadFileFromUrl = async () => {
		setUrlError(null);
		setLoadingProgress(true);

		dispatch(
			SourceActions.validateMediaFile(
				{
					url,
					maxSizeBytes: MAX_FILE_SIZE_BYTES,
					allowedContentTypes: ["video/", "audio/"]
				},
				res => {
					const isValid = res?.isValid || false;

					if (!isValid) {
						const errors = (res?.errors || []).map(err => err.code);
						setLoadingProgress(false);
						setUrlError(
							`${t(`SOURCES.UPLOAD.urlError`)} : ${errors.map(errCode => t(`SOURCES.UPLOAD.ERRORS.${errCode}`)).join(", ")}`
						);
						return;
					}
					setLoadingProgress(false);
					setUrlError(null);
					handleSave({
						localFiles: [],
						files: [
							{
								name: res.url,
								url: res.url,
								alreadyExists: false,
								extra: {
									contentType: res.contentType,
									contentLength: res.contentLength,
									url: res.url,
									external: true
								}
							}
						],
						importMethod: "link"
					});
				},
				err => {
					setLoadingProgress(false);
					setUrlError(t("SOURCES.UPLOAD.urlError"));
				}
			)
		);
	};

	/**
	 * Handle tab change between upload and link
	 */
	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
		setError(null);
		validStep(false);
	};

	/**
	 * Load and validate files for upload
	 */
	const loadFiles = async files => {
		setLoading(true);
		setError(null);

		// Validate file sizes
		const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE_BYTES);
		if (oversizedFiles.length > 0) {
			const fileNames = oversizedFiles.map(file => file.name).join(", ");
			setError(
				t("SOURCES.FILES.fileTooLarge", {
					maxSize: `${MAX_FILE_SIZE_MO}MB`,
					files: fileNames
				})
			);
			setLoading(false);
			return;
		}

		if (lod_.isEmpty(skeleton.files)) {
			const mappedFiles = files.map(file => {
				return {
					name: file.name,
					url: file.name // For local files, the url is the file name
				};
			});

			const filesResult = await checkIfSourcesExist(dispatch, mappedFiles);

			setLoading(false);
			handleSave({
				localFiles: files,
				files: filesResult,
				importMethod: "upload"
			});
		} else {
			setLoading(false);
			validStep();
		}
	};

	/**
	 * Render upload content based on state
	 */
	const renderUploadContent = () => {
		if (loading) {
			return (
				<MDBox
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						flex: 1
					}}
				>
					<MDTypography variant="h4">{t("SOURCES.FILES.loadingFiles")}</MDTypography>
					<MDTypography variant="subtitle2">{t("SOURCES.FILES.waitWhileLoading")}</MDTypography>
					<MDBox display="flex" justifyContent="center" mt={5}>
						<CircularProgress color="info" size={50} />
					</MDBox>
				</MDBox>
			);
		}

		return (
			<DragAndDrop
				handleFiles={files => {
					loadFiles(Array.from(files));
				}}
			/>
		);
	};

	return (
		<MDBox flex="1" display="flex" flexDirection="column" style={{ height: "100%", width: "100%" }}>
			{error && (
				<MDBox sx={{ mb: 2 }}>
					<MDAlert color="error" dismissible onClose={() => setError(null)}>
						{error}
					</MDAlert>
				</MDBox>
			)}

			<Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
				<Tabs value={tabValue} onChange={handleTabChange} centered>
					<Tab
						icon={<CloudUploadIcon />}
						iconPosition="start"
						label={t("SOURCES.VIDEO.uploadFile")}
					/>
					<Tab icon={<LinkIcon />} iconPosition="start" label={t("SOURCES.VIDEO.insertLink")} />
				</Tabs>
			</Box>

			{tabValue === 0 && (
				<MDBox flex="1" display="flex" flexDirection="column">
					{renderUploadContent()}
					<MDTypography variant="caption" color="text" sx={{ mt: 2, textAlign: "center" }}>
						{t("SOURCES.VIDEO.acceptedFormats")}
					</MDTypography>
				</MDBox>
			)}

			{tabValue === 1 && (
				<MDBox display="flex" flex="1" flexDirection="row" justifyContent="center">
					<MDBox sx={{ width: "70%" }}>
						<MDTypography variant="h4">{t("SOURCES.URL.title")}</MDTypography>
						<MDTypography variant="subtitle2">{t("SOURCES.URL.subtitle")}</MDTypography>
						<MDInput
							autoFocus
							sx={{ mt: 5 }}
							disabled={loadingProgress}
							value={url}
							className="dialogInput"
							placeholder={t("SOURCES.UPLOAD.urlPlaceholder")}
							onChange={e => {
								setUrlError("");
								setUrl(e.target.value);
							}}
							onKeyDown={e => {
								if (e.key === "Enter") {
									downloadFileFromUrl();
								}
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{!loadingProgress && (
											<IconButton
												disabled={Boolean(!url)}
												color="info"
												onClick={downloadFileFromUrl}
											>
												<Icon>search</Icon>
											</IconButton>
										)}
										{loadingProgress && <CircularProgress color="info" size={20} />}
									</InputAdornment>
								)
							}}
						/>
						<MDTypography variant="caption" color="text" sx={{ mt: 2 }}>
							{t("SOURCES.VIDEO.linkHint")}
						</MDTypography>
						{/* Loading */}
						{loadingProgress && (
							<Fade in={loadingProgress} timeout={300}>
								<MDBox sx={{ mt: 3 }}>
									<Alert
										icon={
											<CircularProgress
												size={25}
												sx={{ marginRight: 2 }}
												thickness={5}
												color="info"
											/>
										}
										severity="info"
									>
										<MDTypography variant="h6" fontSize="small" color="info">
											{t("SOURCES.URL.loadingUrl")}
										</MDTypography>
									</Alert>
								</MDBox>
							</Fade>
						)}
						{/* Error */}
						{urlError && (
							<Fade in={!lod_.isNil(urlError) && !lod_.isEmpty(urlError)} timeout={300}>
								<MDBox sx={{ mt: 3 }}>
									<Alert icon={<Icon color="error">close</Icon>} severity="error">
										<MDTypography variant="h6" fontSize="small" color="error">
											{urlError}
										</MDTypography>
									</Alert>
								</MDBox>
							</Fade>
						)}
					</MDBox>
				</MDBox>
			)}
		</MDBox>
	);
}
